import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform, useSpring, useMotionValueEvent } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useSuppressFloatingCta } from './floatingCtaContext';
import { buildBrain, IntegrationSphere, LogoStrip, SlackPanel } from './SlackIntegrationGraph';
// ─────────────────────────────────────────────────────────────────────────
// EXPERIMENTAL CARD-MORPH VARIANT
// A scroll-driven twin of <SlackIntegrationGraph>, identical through Act One
// (the spinning integration sphere + "Giga plugs into your world") and Act Two
// (the "Lives inside your chats" chat panel). The ONLY thing that diverges is
// the centre hand-off. Instead of the disc squaring off and shrinking straight
// into the chat avatar, here the beat reads as:
//
//   BEAT 1 — SQUARE:     the glowing round core squares off into a rounded-
//                        square Giga "app icon" with a confident spring.
//   BEAT 2 — CONTAINER:  a card/container background fades + scales in AROUND
//                        the square (a padded white panel with shadow + a faint
//                        chrome bar), so the lone mark visibly grows its own
//                        container, like an app window assembling itself.
//   BEAT 3 — ZOOM OUT:   that whole container then zooms OUT (scales down) as
//                        it glides to the centre of the chat, settling exactly
//                        where Giga's chat avatar lands — the container shrinks
//                        away as the real chat panel takes its place.
//
// Lives only on /chat-transition. The production home choreography is untouched.
// ─────────────────────────────────────────────────────────────────────────
export function SlackIntegrationGraphCardMorph() {
  const reduceMotion = useReducedMotion();
  const brain = useMemo(() => buildBrain(), []);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const {
    scrollYProgress
  } = useScroll({
    target: scrollRef,
    offset: ['start end', 'end end']
  });
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 90,
    damping: 26,
    mass: 0.5
  });
  // ── ACT ONE — title card + sphere (identical to production) ──────────────
  const introOpacity = useTransform(scrollYProgress, [0, 0.08, 0.16, 0.34, 0.42], [0, 0, 1, 1, 0]);
  const introY = useTransform(scrollYProgress, [0.08, 0.16, 0.34, 0.42], [24, 0, 0, -40]);
  const introScale = useTransform(scrollYProgress, [0.08, 0.16, 0.34, 0.42], [0.96, 1, 1, 0.9]);
  const sphereOpacity = useTransform(scrollYProgress, [0, 0.34, 0.46, 0.52, 0.58], [0, 0, 1, 1, 0]);
  const sphereScale = useTransform(scrollYProgress, [0.34, 0.5, 0.58], isMobile ? [1.15, 1.05, 1.05] : [2.15, 2.0, 2.0]);
  const bgOpacity = useTransform(scrollYProgress, [0.5, 0.64], [1, 0]);
  // ── ACT TWO — chat (identical to production) ─────────────────────────────
  // Grows in from a touch-small right as it's revealed (the unit has just
  // landed), so the panel reads as materializing out of the docked avatar
  // rather than sliding/fading in from somewhere else.
  const rawChatScale = useTransform(scrollYProgress, [0.72, 0.78, 0.9], [0.94, 1, 1.04]);
  const chatScale = useSpring(rawChatScale, {
    stiffness: 140,
    damping: 22,
    mass: 0.4
  });
  // The chat panel is SUMMONED from the landed avatar, not cross-faded behind
  // the travelling container. It stays fully hidden while the orange unit flies
  // in (BEAT 3, [0.6, 0.72]) and only resolves over [0.72, 0.745] — right as the
  // unit docks into the avatar slot and the container chrome fades out. So the
  // chat appears to BUILD OUT FROM the logo's position instead of two surfaces
  // (a big floating card over an already-visible chat) being shown at once.
  const chatOpacity = useTransform(scrollYProgress, [0.72, 0.745], [0, 1]);
  const chatHeadingOpacity = useTransform(scrollYProgress, [0.56, 0.66], [0, 1]);
  const chatHeadingY = useTransform(scrollYProgress, [0.56, 0.66], [16, 0]);
  const [active, setActive] = useState(false);
  // ── BRIDGE: measure the chat avatar so the container can land on it ──────
  const chatGigaRef = useRef<HTMLDivElement | null>(null);
  const [avatarRect, setAvatarRect] = useState<{
    x: number;
    y: number;
    size: number;
  } | null>(null);
  useEffect(() => {
    if (reduceMotion) return;
    let raf = 0;
    const measure = () => {
      const el = chatGigaRef.current;
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0) {
          setAvatarRect({
            x: r.left + r.width / 2,
            y: r.top + r.height / 2,
            size: r.width
          });
        }
      }
      raf = requestAnimationFrame(measure);
    };
    raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [reduceMotion]);
  const fallback = typeof window !== 'undefined' ? {
    x: window.innerWidth / 2 - 300,
    y: window.innerHeight * 0.46,
    size: 40
  } : {
    x: 0,
    y: 0,
    size: 40
  };
  const target = avatarRect ?? fallback;
  const STAGE_LIFT = typeof window !== 'undefined' ? window.innerHeight * 0.1 : 80;
  const centerX = typeof window !== 'undefined' ? window.innerWidth / 2 : 0;
  const centerY = typeof window !== 'undefined' ? window.innerHeight / 2 - STAGE_LIFT : 0;
  // The mark's apparent size while it is the live core of the sphere.
  const CORE_SIZE = 150;
  // ── BEAT 1 — square off (time-based, fires once on scroll threshold) ─────
  const [morphed, setMorphed] = useState(false);
  useMotionValueEvent(scrollYProgress, 'change', latest => {
    if (latest >= 0.5 && !morphed) setMorphed(true);else if (latest < 0.44 && morphed) setMorphed(false);
  });
  // ── BEAT 2 — the container card grows in around the square. Scroll-driven
  // so the "container appears" beat sits between squaring (0.5) and travel
  // (0.6): the card fades + springs up from nothing while the mark holds at
  // centre, reading as the icon assembling its own window. ─────────────────
  const containerProgressRaw = useTransform(scrollYProgress, [0.5, 0.6], [0, 1]);
  const containerProgress = useSpring(containerProgressRaw, {
    stiffness: 160,
    damping: 24,
    mass: 0.6
  });
  const containerOpacity = useTransform(containerProgress, [0, 0.25, 1], [0, 1, 1]);
  // The container scales UP from 0.6 → full as it appears (it grows in), then
  // BEAT 3 scales the whole travelling group DOWN as it flies to the avatar.
  const containerScale = useTransform(containerProgress, [0, 1], [0.6, 1]);
  // ── BEAT 3 — zoom out + magnetize into the avatar slot ───────────────────
  const landProgress = useTransform(scrollYProgress, [0.6, 0.72], [0, 1]);
  const land = useSpring(landProgress, {
    stiffness: 130,
    damping: 20,
    mass: 0.7
  });
  // While the container is shown, the travelling "unit" is the CARD, which is
  // larger than the bare mark. So the box we position is the container box: it
  // starts large around the core and zooms down to the avatar size on landing.
  // Container is sized as a padded multiple of the inner mark.
  const CONTAINER_START = CORE_SIZE * 1.7;
  const bridgeBoxSize = useTransform(land, [0, 1], [CONTAINER_START, target.size]);
  const bridgeCx = useTransform(land, [0, 1], [centerX, target.x]);
  const bridgeCy = useTransform(land, [0, 1], [centerY, target.y]);
  const bridgeX = useTransform([bridgeCx, bridgeBoxSize] as const, ([cx, s]: number[]) => cx - s / 2);
  const bridgeY = useTransform([bridgeCy, bridgeBoxSize] as const, ([cy, s]: number[]) => cy - s / 2);
  // The inner Giga square moves from FILLING the box (when it's still the bare
  // core, before the container) → settling into the chat AVATAR slot in the
  // upper-left of the chat surface as the container grows in → then re-filling
  // the box as the container zooms away, so what finally lands is the clean
  // orange avatar square (not a tiny mark in a big card).
  //
  // Per-side inset targets while the container is fully present: a ~24%-size
  // square pinned to the upper-left, like a chat message avatar.
  const AVATAR_TOP = 0.2;
  const AVATAR_LEFT = 0.14;
  const AVATAR_RIGHT = 0.62;
  const AVATAR_BOTTOM = 0.62;
  const makeInset = (target: number) => {
    // Grows from 0 (filling the box as the bare core) → target (avatar slot) as
    // the container appears, then shrinks back to 0 as the unit zooms away.
    const grow = useTransform(containerProgress, [0, 1], [0, target]);
    const shrink = useTransform(land, [0, 1], [target, 0]);
    const value = useTransform([grow, shrink, land] as const, ([g, s, l]: number[]) => l > 0 ? s : g);
    return useTransform(value, (v: number) => `${v * 100}%`);
  };
  const markTopPct = makeInset(AVATAR_TOP);
  const markLeftPct = makeInset(AVATAR_LEFT);
  const markRightPct = makeInset(AVATAR_RIGHT);
  const markBottomPct = makeInset(AVATAR_BOTTOM);
  // The faint chat skeleton fades out as the unit zooms away so the very last
  // thing that lands is a clean avatar square, not a shrunken chat card.
  const chromeOpacity = useTransform(land, [0, 0.4, 1], [1, 1, 0]);
  // ── HAND-OFF: hard-cut to the real chat avatar once the card has landed ──
  const chatAvatarReveal = useTransform(scrollYProgress, [0.728, 0.73], [0, 1]);
  const bridgeOpacity = useTransform(scrollYProgress, [0.34, 0.46, 0.73, 0.735], [0, 1, 1, 0]);
  const [landed, setLanded] = useState(false);
  useMotionValueEvent(scrollYProgress, 'change', latest => {
    if (latest >= 0.735 && !landed) setLanded(true);else if (latest < 0.68 && landed) setLanded(false);
  });
  // Suppress the global floating CTA during the integration beat.
  const [inIntegrationBeat, setInIntegrationBeat] = useState(false);
  useMotionValueEvent(scrollYProgress, 'change', latest => {
    if (!inIntegrationBeat && latest >= 0.3 && latest < 0.6) {
      setInIntegrationBeat(true);
    } else if (inIntegrationBeat && (latest >= 0.62 || latest < 0.26)) {
      setInIntegrationBeat(false);
    }
  });
  useSuppressFloatingCta(!reduceMotion && inIntegrationBeat);
  // Warm halo behind the mark — strongest as the live core, eases off as the
  // container assembles and the unit magnetizes into the avatar.
  const bridgeGlow = useTransform([containerProgress, land] as const, ([c, l]: number[]) => Math.max(0, 0.55 - c * 0.3 - l * 0.25));
  return <div ref={scrollRef} className="relative w-full" style={{
    height: reduceMotion ? undefined : '300vh'
  }} data-id="element-2694">
      <section className="sticky top-0 h-screen w-full overflow-hidden flex flex-col bg-[#FAF8F4]" data-id="element-2695">
        <motion.div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
        opacity: reduceMotion ? 0 : bgOpacity,
        background: 'linear-gradient(180deg, #271D17 0%, #2B201A 50%, #241A14 100%)'
      }} data-id="element-2696" />
        <motion.div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
        opacity: reduceMotion ? 0 : bgOpacity,
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.45'/%3E%3C/svg%3E\")",
        mixBlendMode: 'soft-light'
      }} data-id="element-2697" />
        <motion.div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[34%]" style={{
        opacity: reduceMotion ? 0 : introOpacity,
        background: 'linear-gradient(to bottom, rgba(20,14,9,0.55), rgba(20,14,9,0) 100%)'
      }} data-id="element-2698" />

        {/* ACT ONE — title card */}
        <motion.div className="pointer-events-none absolute inset-0 z-30 flex flex-col items-center justify-center text-center px-6" style={{
        opacity: reduceMotion ? 0 : introOpacity,
        y: reduceMotion ? 0 : introY,
        scale: reduceMotion ? 1 : introScale
      }} data-id="element-2699">
          <span className="block text-xs sm:text-sm font-semibold tracking-[5px] uppercase text-white/50 mb-4 md:mb-6" data-id="element-2700">
            Works where you work
          </span>
          <h2 className="max-w-4xl text-4xl sm:text-6xl md:text-7xl font-title font-medium tracking-tight leading-[1.05] text-white" data-id="element-2701">
            Giga plugs into your world.
          </h2>
        </motion.div>

        {/* ACT ONE — integration sphere */}
        <motion.div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center" onViewportEnter={() => setActive(true)} style={{
        opacity: reduceMotion ? 0 : sphereOpacity,
        scale: reduceMotion ? 1 : sphereScale,
        y: reduceMotion ? 0 : -STAGE_LIFT
      }} data-id="element-2702">
          <IntegrationSphere brain={brain} smoothProgress={smoothProgress} active={active || !!reduceMotion} reduceMotion={!!reduceMotion} data-id="element-2703" />
        </motion.div>

        {/* ACT ONE — logo strip */}
        <motion.div className="pointer-events-none absolute inset-x-0 bottom-[16%] sm:bottom-[15%] z-30 flex justify-center px-6" style={{
        opacity: reduceMotion ? 0 : sphereOpacity
      }} data-id="element-2704">
          <LogoStrip reduceMotion={!!reduceMotion} data-id="element-2705" />
        </motion.div>

        {/* ACT TWO — chat headline */}
        <motion.div className="pointer-events-none absolute top-[8%] sm:top-[10%] left-0 right-0 z-30 text-center px-6" style={{
        opacity: reduceMotion ? 1 : chatHeadingOpacity,
        y: reduceMotion ? 0 : chatHeadingY
      }} data-id="element-2706">
          <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-3 md:mb-4" data-id="element-2707">
            No new app to learn
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-title font-medium tracking-tight text-[#2B211A]" data-id="element-2708">
            Lives inside your chats.
          </h2>
        </motion.div>

        {/* ACT TWO — chat panel */}
        <motion.div className="absolute inset-x-0 top-[23%] sm:top-[28%] z-30 flex justify-center px-4" style={{
        transformOrigin: 'center top',
        scale: reduceMotion ? 1 : chatScale,
        opacity: reduceMotion ? 1 : chatOpacity
      }} data-id="element-2709">
          <SlackPanel gigaRef={chatGigaRef} avatarReveal={reduceMotion ? 1 : chatAvatarReveal} responseReady={reduceMotion ? true : landed} data-id="element-2710" />
        </motion.div>

        {/* THE SINGLE GIGA UNIT — core → square → container card → zooms into
            the chat avatar. One element the whole way, so there's never a
            duplicate logo. The outer box IS the travelling container; the
            orange square mark sits inside it with animated padding. */}
        {!reduceMotion && <motion.div aria-hidden="true" className="pointer-events-none absolute left-0 top-0 z-[45]" style={{
        width: bridgeBoxSize,
        height: bridgeBoxSize,
        x: bridgeX,
        y: bridgeY,
        opacity: bridgeOpacity
      }} data-id="element-2711">
            {/* Warm halo behind the unit */}
            <motion.span aria-hidden="true" className="pointer-events-none absolute -inset-[30%] rounded-full" style={{
          opacity: bridgeGlow,
          background: 'radial-gradient(circle, rgba(234,88,12,0.55) 0%, rgba(234,88,12,0.18) 38%, rgba(234,88,12,0) 70%)'
        }} data-id="element-2712" />

            {/* THE CHAT-SURFACE CONTAINER — fades + grows in around the square
                (BEAT 2), then zooms out with the whole group (BEAT 3). Its
                background reads as the CHAT itself materializing: the same
                white chat surface as the panel below, with a faint message-row
                skeleton beside the avatar slot, so the lone mark visibly
                becomes a chat avatar sitting in a chat — not a generic app
                window. */}
            <motion.div className="absolute inset-0 rounded-[22%] bg-white border border-[#E7E0D7] overflow-hidden" style={{
          opacity: containerOpacity,
          scale: containerScale,
          transformOrigin: 'center center',
          boxShadow: '0 30px 70px -20px rgba(43,33,26,0.45)'
        }} data-id="element-2713">
              {/* Faint chat-message skeleton beside where the avatar sits — a
               sender-name line + two reply lines — so the surface reads as a
               chat conversation materializing, not a generic app window. */}
              <motion.div className="absolute flex flex-col gap-[6%]" style={{
            top: '22%',
            left: '46%',
            right: '14%',
            opacity: chromeOpacity
          }} data-id="element-2714">
                <span className="h-[6px] w-[55%] rounded-full bg-[#E7DFD5]" data-id="element-2715" />
                <span className="h-[6px] w-[90%] rounded-full bg-[#F1ECE4]" data-id="element-2716" />
                <span className="h-[6px] w-[68%] rounded-full bg-[#F1ECE4]" data-id="element-2717" />
              </motion.div>
            </motion.div>

            {/* THE GIGA SQUARE MARK — the disc squares off (BEAT 1) on a single
                confident spring, then settles into the chat AVATAR position
                (upper-left) inside the surface, reading as the avatar in place.
                As the container zooms away (BEAT 3) the mark re-centers and
                fills the box so what lands is the clean orange avatar square. */}
            <motion.div className="absolute flex items-center justify-center bg-[#EA580C] shadow-[0_8px_30px_rgba(234,88,12,0.45)]" style={{
          top: markTopPct,
          left: markLeftPct,
          right: markRightPct,
          bottom: markBottomPct
        }} initial={false} animate={morphed ? {
          borderRadius: '24%',
          scale: [1, 1.04, 1]
        } : {
          borderRadius: '50%',
          scale: 1
        }} transition={morphed ? {
          borderRadius: {
            type: 'spring',
            stiffness: 200,
            damping: 26,
            mass: 0.8
          },
          scale: {
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1],
            times: [0, 0.45, 1]
          }
        } : {
          duration: 0.3,
          ease: 'easeOut'
        }} data-id="element-2718">
              <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="" className="w-[55%] h-[55%] object-contain [filter:brightness(0)_invert(1)]" data-id="element-2719" />
            </motion.div>
          </motion.div>}

        {/* Scroll hint — Act One only */}
        <motion.div className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-1.5 text-white/50" style={{
        opacity: reduceMotion ? 0 : introOpacity
      }} data-id="element-2720">
          <span className="text-[11px] font-semibold uppercase tracking-[3px]" data-id="element-2721">
            Scroll
          </span>
          <motion.span animate={{
          y: [0, 6, 0]
        }} transition={{
          duration: 1.6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-2722">
            <ChevronDown size={18} strokeWidth={2.25} data-id="element-2723" />
          </motion.span>
        </motion.div>
      </section>
    </div>;
}